import React, { useState, useEffect, useRef, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import Layout from "../components/Layout";
import "./UserList.css";
import "./WeeklyCommission.css";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ADMIN_BASE_URL, API_BASE_URL } from "./config";
import { FiCopy } from "react-icons/fi";
import { toast } from "react-toastify";

function ReferralAndEarningsList() {
    const gridRef = useRef(null);
    const [activeTab, setActiveTab] = useState("users");
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    //Referred User
    const [referredUsers, setReferredUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [usersError, setUsersError] = useState(null);
    // Commission hierarchy (from user/commission-hierarchy API)
    const [hierarchy, setHierarchy] = useState([]);
    const [earningsLoading, setEarningsLoading] = useState(true);
    const [earningsError, setEarningsError] = useState(null);
    const [expandedEarningsRowKey, setExpandedEarningsRowKey] = useState(null);

    const currentUserId = localStorage.getItem("userId");
    const token = localStorage.getItem("token");

    const referralLink = "https://viron.com/ref/USER123";


    const referredUsersColumns = useMemo(
        () => [
            {
                headerName: "Member ID",
                field: "id",
                flex: 1,
                minWidth: 80,
            },
            {
                headerName: "User Name",
                field: "user_name",
                flex: 1,
                minWidth: 140,
                cellRenderer: (params) => (
                    <span
                    // style={{
                    //     color: "#1976d2",
                    //     textDecoration: "underline",
                    //     cursor: "pointer",
                    //     fontWeight: 500,
                    // }}
                    >
                        {params.value}
                    </span>
                ),
            },
            {
                headerName: "First Name",
                field: "first_name",
                flex: 1,
                minWidth: 120,
            },
            {
                headerName: "Last Name",
                field: "last_name",
                flex: 1,
                minWidth: 120,
            },
            {
                headerName: "Country",
                field: "country",
                flex: 1,
                minWidth: 140,
                cellRenderer: (params) => {
                    const handleCopy = () => {
                        navigator.clipboard.writeText(params.value);
                        toast.success("Copied to clipboard!");
                    };

                    return (
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>{params.value || "-"}</span>
                            {params.value && (
                                <button
                                    onClick={handleCopy}
                                    style={{ border: "none", background: "none", cursor: "pointer" }}
                                >
                                    <FiCopy size={14} />
                                </button>
                            )}
                        </div>
                    );
                },
            },
            {
                headerName: "City",
                field: "city",
                flex: 1,
                minWidth: 140,
            },
            {
                headerName: "Registration Date",
                field: "created_at",
                flex: 1,
                minWidth: 150,
                valueFormatter: (params) =>
                    params.value
                        ? new Date(params.value).toLocaleDateString("en-US")
                        : "—",
            },
        ],
        []
    );

    const fetchReferralData = async () => {
        setUsersLoading(true);
        setEarningsLoading(true);
        setUsersError(null);
        setEarningsError(null);

        try {
            const [usersRes, earningsRes] = await Promise.all([
                fetch(`${API_BASE_URL}/referred-users/${currentUserId}`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }),
                fetch(`${ADMIN_BASE_URL}/usercommission/user/commission-hierarchy`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }),
            ]);

            /* ---------- USERS API ---------- */
            if (!usersRes.ok) {
                throw new Error("Failed to load referred users");
            }
            const usersData = await usersRes.json();
            setReferredUsers(usersData || []);
            setUsersLoading(false);

            /* ---------- EARNINGS: commission-hierarchy API ---------- */
            if (!earningsRes.ok) {
                throw new Error("Failed to load commission hierarchy");
            }
            const earningsJson = await earningsRes.json();
            if (!earningsJson.success) {
                throw new Error(earningsJson.message || "Failed to load commission hierarchy");
            }
            setHierarchy(earningsJson.hierarchy || []);
            setEarningsLoading(false);

        } catch (error) {
            console.error("API Error:", error);
            setUsersError(error.message);
            setEarningsError(error.message);
            setUsersLoading(false);
            setEarningsLoading(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReferralData();
    }, [currentUserId, token]);

    // Build summary rows from hierarchy (one per week/earner, same as WeeklyCommission)
    const earningsSummaryRows = [];
    hierarchy.forEach((week, weekIndex) => {
        (week.children || []).forEach((earner, earnerIndex) => {
            const firstReferred = earner.children?.[0]?.name || "—";
            earningsSummaryRows.push({
                key: `earnings-${weekIndex}-${earnerIndex}`,
                weekIndex,
                earnerIndex,
                weekName: week.name,
                userName: earner.name,
                referredTo: firstReferred,
                amount: earner.branchTotal ?? 0,
                status: earner.branchStatus || earner.paymentStatus || "pending",
                earner,
            });
        });
    });

    const toggleEarningsRow = (key) => {
        setExpandedEarningsRowKey((prev) => (prev === key ? null : key));
    };

    const formatStatusLabel = (s) => {
        if (s === "partially_paid") return "Partially Paid";
        if (s === "paid") return "Completed";
        if (s === "pending") return "Not Started";
        return (s || "pending").charAt(0).toUpperCase() + (s || "pending").slice(1);
    };

    const columnDefs = referredUsersColumns;

    return (
        <Layout>
            <div className="userlist-container weekly-commission" style={{ marginTop: 20 }}>
                <header className="weekly-commission-header">
                    <h2 className="weekly-commission-title">My Referrals & Earnings</h2>
                    <p className="weekly-commission-subtitle">View your referred users and weekly referral earnings</p>
                </header>

                {/* Tabs */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginTop: '20px',
                        gap: "20px",
                        flexWrap: "wrap",
                    }}
                >
                    {/* LEFT SIDE – TABS */}
                    <div
                        style={{
                            display: 'flex',
                            gap: '8px',
                            borderBottom: '1px solid #e0e0e0',
                            paddingBottom: '8px',
                        }}
                    >
                        <button
                            onClick={() => setActiveTab("users")}
                            style={{
                                padding: '10px 20px',
                                border: 'none',
                                borderRadius: '6px 6px 0 0',
                                backgroundColor: activeTab === 'users' ? '#1976d2' : '#f5f5f5',
                                color: activeTab === 'users' ? '#fff' : '#333',
                                fontWeight: 600,
                                fontSize: 14,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                borderBottom: activeTab === 'users'
                                    ? '3px solid #1976d2'
                                    : '3px solid transparent'
                            }}
                        >
                            My Referred Users ({referredUsers.length})
                        </button>

                        <button
                            onClick={() => setActiveTab("earnings")}
                            style={{
                                padding: '10px 20px',
                                border: 'none',
                                borderRadius: '6px 6px 0 0',
                                backgroundColor: activeTab === 'earnings' ? '#1976d2' : '#f5f5f5',
                                color: activeTab === 'earnings' ? '#fff' : '#333',
                                fontWeight: 600,
                                fontSize: 14,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                borderBottom: activeTab === 'earnings'
                                    ? '3px solid #1976d2'
                                    : '3px solid transparent'
                            }}
                        >
                            My Referred Earnings ({earningsSummaryRows.length})
                        </button>
                    </div>

                    {/* RIGHT SIDE – SEARCH OR REFERRAL LINK */}
                    <div style={{ minWidth: 300, flexGrow: 1, maxWidth: 500, marginTop: '-12px' }}>
                        {activeTab === 'users' && (
                            <div>
                                <label
                                    htmlFor="search"
                                    style={{
                                        display: 'block',
                                        marginBottom: 6,
                                        fontSize: 14,
                                        color: '#37474f',
                                        fontWeight: 600,
                                    }}
                                >
                                    Search
                                </label>
                                <input
                                    id="search"
                                    type="text"
                                    placeholder="Search"
                                    value={searchText}
                                    onChange={e => {
                                        setSearchText(e.target.value);
                                        if (gridRef.current?.api?.setQuickFilter) {
                                            gridRef.current.api.setQuickFilter(e.target.value);
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        borderRadius: 6,
                                        border: '1.5px solid #90a4ae',
                                        fontSize: 15,
                                        outline: 'none',
                                    }}
                                />
                            </div>
                        )}

                        {activeTab !== 'users' && (
                            <div
                            // style={{
                            //     background: "#f9fafb",
                            //     border: "1px solid #e0e0e0",
                            //     borderRadius: 8,
                            //     padding: 16,
                            // }}
                            >
                                <div style={{ fontWeight: 600, marginBottom: 8 }}>
                                    Your Referral Link
                                </div>

                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 12,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <div
                                        style={{
                                            background: "#ffffff",
                                            border: "1px solid #d0d5dd",
                                            padding: "8px 12px",
                                            borderRadius: 6,
                                            flex: 1,
                                            minWidth: 200,
                                            fontSize: 14,
                                        }}
                                    >
                                        {referralLink}
                                    </div>

                                    <button
                                        style={{
                                            padding: "8px 16px",
                                            borderRadius: 6,
                                            border: "none",
                                            background: "#1976d2",
                                            color: "#fff",
                                            cursor: "pointer",
                                            fontWeight: 500,
                                        }}
                                    >
                                        Copy Link
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>


                {/* Users tab: AG Grid | Earnings tab: WeeklyCommission-style table */}

                {activeTab === "users" && (
                    <div style={{ width: "100%", marginTop: 20 }}>
                        <div className="ag-theme-alpine">
                            <AgGridReact
                                ref={gridRef}
                                onGridReady={params => {
                                    gridRef.current = params;
                                }}
                                quickFilterText={searchText}
                                theme="legacy"
                                rowData={referredUsers}
                                columnDefs={columnDefs}
                                pagination={true}
                                animateRows={true}
                                paginationPageSize={1000}
                                paginationPageSizeSelector={[20, 50, 100, 500, 1000]}
                                suppressHorizontalScroll={false}
                                domLayout="autoHeight"
                            />
                        </div>
                    </div>
                )}

                {activeTab === "earnings" && (
                    <>
                        {earningsLoading ? (
                            <div className="weekly-commission-loading" style={{ marginTop: 20 }}>
                                <div className="weekly-commission-spinner" aria-hidden />
                                <p>Loading referral earnings...</p>
                            </div>
                        ) : hierarchy.length === 0 ? (
                            <div className="weekly-commission-empty" style={{ marginTop: 20 }}>
                                <p className="weekly-commission-empty-title">No referral earnings</p>
                                <p className="weekly-commission-empty-text">Earnings will appear here once you have referrals and commissions.</p>
                            </div>
                        ) : (
                            <div className="commission-drilldown-table-wrapper" style={{ marginTop: 20 }}>
                                <table className="commission-drilldown-table">
                                    <thead>
                                        <tr>
                                            <th className="col-user">Week</th>
                                            <th className="col-user">User Name</th>
                                            <th className="col-referred">Referred To</th>
                                            <th className="col-amount">Commission Amount</th>
                                            <th className="col-status">Payment Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {earningsSummaryRows.map((row, index) => (
                                            <React.Fragment key={row.key}>
                                                <tr
                                                    className={`commission-summary-row ${index % 2 === 1 ? "row-alt" : ""}`}
                                                    onClick={() => toggleEarningsRow(row.key)}
                                                >
                                                    <td className="col-week">
                                                        <span className="drilldown-chevron" aria-hidden>
                                                            <ChevronIcon expanded={expandedEarningsRowKey === row.key} />
                                                        </span>
                                                        {row.weekName}
                                                    </td>
                                                    <td className="col-user">{row.userName}</td>
                                                    <td className="col-referred">{row.referredTo}</td>
                                                    <td className="col-amount amount-cell">
                                                        ${Number(row.amount).toFixed(2)}
                                                    </td>
                                                    <td className="col-status">
                                                        <span className={`status-pill status-${row.status === "paid" ? "completed" : row.status === "partially_paid" ? "partially_paid" : "not_started"}`}>
                                                            {row.status === "paid" && <CheckIcon size={14} />}
                                                            {formatStatusLabel(row.status)}
                                                        </span>
                                                    </td>
                                                </tr>
                                                {expandedEarningsRowKey === row.key && (
                                                    <tr className="commission-detail-row">
                                                        <td colSpan={5}>
                                                            <div className="referral-tree-section">
                                                                <div className="referral-tree-title">REFERRAL TREE</div>
                                                                <div className="tree-view">
                                                                    <div className="tree-node tree-node--parent">
                                                                        <span className="tree-node-icon" aria-hidden>
                                                                            <TreeIcon />
                                                                        </span>
                                                                        <span className="tree-node-label">{row.earner.name}</span>
                                                                    </div>
                                                                    <div className="tree-children">
                                                                        {(row.earner.children || []).map((node) => (
                                                                            <EarningsTreeNode
                                                                                key={`${node.userId}-${node.commission_id || ""}-${node.paymentStatus || "pending"}`}
                                                                                node={node}
                                                                                depth={0}
                                                                            />
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {loading && activeTab === "users" && (
                    <div style={{ marginTop: 10 }}>Loading data...</div>
                )}
            </div>
        </Layout>
    );
}

function CheckIcon({ size = 18 }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
        </svg>
    );
}

function ChevronIcon({ expanded }) {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="currentColor"
            width={16}
            height={16}
            className={`drilldown-chevron-icon ${expanded ? "expanded" : ""}`}
            aria-hidden
        >
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z" />
        </svg>
    );
}

function TreeIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" width={20} height={20}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
        </svg>
    );
}

/** Read-only commission card for user earnings tree (no edit inputs). Renders even when amount is 0 so full hierarchy shows in each week. */
function EarningsCommissionCard({ node }) {
    if (!node || node.isWeek || node.isEarner) return null;
    const amount = node.amount || 0;
    const isPaid = node.paymentStatus === "paid";
    const levelNum = node.levelOrdinal != null ? node.levelOrdinal : (node.treeLevel != null ? node.treeLevel : 1);
    return (
        <div className={`commission-card ${isPaid ? "paid" : "pending"}`}>
            <div className="commission-card-top">
                <div className="commission-card-user">
                    <span className={`level-dot l${levelNum}`} data-level={levelNum} />
                    <span className="name">{node.name}</span>
                    {(node.referralUserName != null && node.referralUserName !== '') && (
                        <div className="referred-by">Referred by: {node.referralUserName}</div>
                    )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="level-pill">L{levelNum}</span>
                    <span className={`commission-amount ${isPaid ? "paid" : "pending"}`}>
                        ${Number(amount).toFixed(2)}
                    </span>
                </div>
            </div>
        </div>
    );
}

function EarningsTreeNode({ node, depth = 0 }) {
    if (!node || node.isWeek || node.isEarner) return null;
    const hasChildren = node.children && node.children.length > 0;
    const children = node.children || [];
    return (
        <div className={`tree-node-wrapper ${depth > 0 ? "tree-node-wrapper--nested" : ""}`} data-depth={depth}>
            <div className="tree-node tree-node--item">
                <EarningsCommissionCard node={node} depth={depth} />
            </div>
            {hasChildren && (
                <div className="tree-children">
                    {children.map((child) => (
                        <EarningsTreeNode
                            key={`${child.userId}-${child.commission_id || ""}-${child.paymentStatus || "pending"}`}
                            node={child}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default ReferralAndEarningsList;
